/* import __COLOCATED_TEMPLATE__ from './wrap-up-banner.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import type AdminAwayService from 'embercom/services/admin-away-service';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import type Snackbar from 'embercom/services/snackbar';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import { PhoneAwayStatusReasonTypes } from 'embercom/services/admin-away-service';
import { action } from '@ember/object';

interface Args {}

interface Signature {
  Element: HTMLButtonElement;
  Args: Args;
}

export default class WrapUpBanner extends Component<Signature> {
  @service declare adminAwayService: AdminAwayService;
  @service declare snackbar: Snackbar;
  @service declare intl: IntlService;
  @service declare session: Session;
  @tracked showSuccessBanner = false;
  @tracked currentTime = Date.now();

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    let timer = setInterval(() => (this.currentTime = Date.now()), 1000);
    registerDestructor(this, () => clearInterval(timer));
  }

  get shouldDisplayBanner() {
    return (
      !this.hasExpired &&
      this.adminAwayService.awayModeEnabled &&
      this.adminAwayService.isCurrentStatusAssociatedToPhoneStatus(
        PhoneAwayStatusReasonTypes.WrapUp,
      )
    );
  }

  get isTimerEnabled() {
    return this.session.workspace?.isFeatureEnabled('phone-acw-timer-banner') ?? false;
  }

  get shouldDisplayTimer() {
    return this.isTimerEnabled && !!this.adminAwayService.statusChangedAt;
  }

  get elapsedSeconds(): number {
    let startedAt = this.adminAwayService.statusChangedAt;
    if (!startedAt) {
      return 0;
    }
    let elapsedMs = this.currentTime - startedAt.getTime();
    return Math.max(0, Math.floor(elapsedMs / 1000));
  }

  get remainingSeconds(): number {
    let timeout = this.adminAwayService.wrapUpTimeoutSeconds;
    if (timeout == null) {
      return 0;
    }
    return Math.max(0, timeout - this.elapsedSeconds);
  }

  // Hide immediately on expiry; backend flips status async.
  get hasExpired(): boolean {
    if (!this.isTimerEnabled) {
      return false;
    }
    let timeout = this.adminAwayService.wrapUpTimeoutSeconds;
    if (timeout == null || !this.adminAwayService.statusChangedAt) {
      return false;
    }
    return this.elapsedSeconds >= timeout;
  }

  get timerText(): string {
    let hasTimeout = this.adminAwayService.wrapUpTimeoutSeconds != null;
    let seconds = hasTimeout ? this.remainingSeconds : this.elapsedSeconds;

    let formatted = formatMMSS(seconds);

    if (hasTimeout) {
      return this.intl.t('inbox.wrap-up-banner.timer-remaining', { time: formatted });
    }
    return this.intl.t('inbox.wrap-up-banner.timer-elapsed', { time: formatted });
  }

  @action setAvailable() {
    this.adminAwayService.toggleAwayMode();
    this.snackbar.notify(this.intl.t('inbox.wrap-up-banner.switch-to-active-success'));
  }
}

function formatMMSS(totalSeconds: number): string {
  let minutes = Math.floor(totalSeconds / 60);
  let seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::WrapUpBanner': typeof WrapUpBanner;
  }
}
